import { useMutation, useQueryClient } from "@tanstack/react-query";
import { UserProfileDto, UserRole } from "@green0meter/api-client";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";

import { DOMAIN_DOUBLE_MATERIALITY } from "../domain";

import { roleGuard } from "@api";
import { doubleMaterialityAssessmentsApi } from "@api/services";

export const ACTION_DELETE_ASSESSMENT = "DELETE_ASSESSMENT";
const ACTION_GET_ASSESSMENTS = "GET_ASSESSMENTS";

const allowedRoles = [UserRole.Owner, UserRole.Manager];

type DeleteAssessmentRequest = {
  id: string;
  deleteQuestions: boolean;
};

const deleteAssessment = async (request: DeleteAssessmentRequest): Promise<void> => {
  const { id, deleteQuestions } = request;

  await doubleMaterialityAssessmentsApi.doubleMaterialityAssessmentsControllerDeleteAssessment(
    id,
    deleteQuestions,
  );
};

export const useDeleteAssessment = (
  user: UserProfileDto | null,
): {
  deleteAssessment: (request: DeleteAssessmentRequest) => void;
  deleteAssessmentAsync: (request: DeleteAssessmentRequest) => Promise<void>;
  isPendingDeleteAssessment: boolean;
  isSuccessDeleteAssessment: boolean;
  isErrorDeleteAssessment: boolean;
  errorDeleteAssessment: Error | null;
  enabled: boolean;
} => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const enabled = roleGuard(user, allowedRoles);

  const mutation = useMutation({
    mutationFn: deleteAssessment,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [DOMAIN_DOUBLE_MATERIALITY, ACTION_GET_ASSESSMENTS],
      });
      enqueueSnackbar(t("assessments.messages.deleteSuccess"), {
        variant: "success",
      });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || t("assessments.messages.deleteError"), {
        variant: "error",
      });
    },
  });

  return {
    deleteAssessment: mutation.mutate,
    deleteAssessmentAsync: mutation.mutateAsync,
    isPendingDeleteAssessment: mutation.isPending,
    isSuccessDeleteAssessment: mutation.isSuccess,
    isErrorDeleteAssessment: mutation.isError,
    errorDeleteAssessment: mutation.error,
    enabled,
  };
};
